<template>
  <div class="notifications">
    <div class="notifications__header container">
      <h1 class="notifications__header-title">
        {{ $t("notification.title") }}
      </h1>
    </div>

    <div class="notifications__content">
      <div class="container">
        <div class="search-notifications">
          <SearchField
            v-click-outside="hideSearchList"
            v-model="searchText"
            class="search-notifications__search"
            :placeholder="$t('notification.searchCompany')"
            @input="changeSearchCompanies"
            @focus="getListCompaniesSearch"
            :autocomplete="isShowSearchList"
            :autocompleteList="listSearchCompany"
            @autocompleteClick="selectCompany"
          >
          </SearchField>
        </div>
        <div class="settings-notifications">
          <div v-if="events?.length" class="settings-notifications__filter">
            <div class="settings-notifications__filter-title">
              {{ $t("notification.eventNameInputPlaceholder") }}
            </div>
            <Dropdown
              v-model="events"
              class="dropdown_black-arr settings-notifications__filter-field"
              single
              @input="updateListNotification(1)"
            />
          </div>

          <div v-if="categories?.length" class="settings-notifications__filter">
            <div class="settings-notifications__filter-title">
              {{ $t("notification.typeNotificationTitle") }}
            </div>
            <Dropdown
              v-model="categories"
              class="dropdown_black-arr settings-notifications__filter-field"
              single
              @input="updateListNotification(1)"
            />
          </div>

          <div v-if="countUnreadItems" class="settings-notifications__btn-wrap">
            <div
              class="settings-notifications__btn-select-all"
              @click="setReadAllNotification()"
            >
              <icon width="20px" height="18px" name="check-letter" />
              {{ $t("notification.readAllNotification") }}
            </div>
          </div>
        </div>

        <NotificationsList
          :list-notification="listNotification"
          :is-loading="showLoader"
        />
      </div>
      <Pagination
        v-if="countPageNotification > 1"
        class="events-pagination"
        :page="currentPage"
        @change="paginate"
        :page-count="countPageNotification"
        :total-visible="paginationPageAmount"
      />
    </div>

    <Loader v-if="showLoader" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import {
  Dropdown,
  Loader,
  SearchField,
  Pagination
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import { NotificationsList } from "@v2/entities/notification";

export default {
  name: "Notifications",

  components: {
    Pagination,
    Loader,
    Dropdown,
    SearchField,
    NotificationsList
  },

  data() {
    return {
      categories: [],
      events: [],
      currentPage: +this.$route.query?.page || 1,
      perPage: 8,
      showLoader: false,
      searchText: "",
      selectedCompany: null,
      listCompanies: [],
      listSearchCompany: [],
      isShowSearchList: false
    };
  },

  computed: {
    ...mapState("notification", [
      "categoriesNotificationEmail",
      "eventsSummaryNotification",
      "companiesSummaryNotification",
      "listNotificationAll",
      "countPageNotification",
      "countUnreadItems"
    ]),

    locale() {
      return i18n.locale;
    },
    paginationPageAmount() {
      return window.innerWidth > 570 ? 5 : 2;
    },
    listNotification() {
      return this.listNotificationAll.map(notification => {
        return {
          id: notification.id,
          createdAt: notification.created_at
            ? moment(notification.created_at).format("DD.MM.YYYY")
            : null,
          isNew: notification.read_at === null,
          title:
            i18n.locale === "en"
              ? notification.data.message_eng
              : notification.data.message_rus
        };
      });
    },

    selectedCategories() {
      return this.categories
        ? this.categories.find(el => el.selected)?.value
        : null;
    },

    selectedEvents() {
      return this.events.length
        ? this.events.find(el => el.selected).value
        : null;
    },

    apiParams() {
      return {
        page: this.currentPage,
        perPage: this.perPage,
        notificationType: this.selectedCategories,
        companyId: this.selectedCompany?.id || null,
        exhibitionId: this.selectedEvents
      };
    }
  },

  watch: {
    locale() {
      this.events = [];
      this.categories = [];

      this.$nextTick(() => {
        this.initEvents();
        this.initCategories();
      });
    },
    categoriesNotificationEmail: {
      deep: true,
      handler(val) {
        if (val.length && !this.categories.length) {
          this.initCategories();
        }
      }
    },
    eventsSummaryNotification: {
      deep: true,
      handler(val) {
        if (val.length && !this.events.length) {
          this.initEvents();
        }
      }
    },
    companiesSummaryNotification: {
      deep: true,
      handler(val) {
        if (val.length && !this.listCompanies.length) {
          this.initCompanies();
        }
      }
    },
    selectedCompany: {
      deep: true,
      handler() {
        this.updateListNotification(1);
      }
    }
  },

  created() {
    this.categories = [];
    this.events = [];
    this.listCompanies = [];
    this.listSearchCompany = [];
    this.$store.dispatch("notification/getCategoriesNotificationEmail");
    this.$store.dispatch("notification/getExhibitions");
    this.$store.dispatch("notification/getCompanies");
  },

  mounted() {
    if (this.categoriesNotificationEmail.length) {
      this.initCategories();
    }

    if (this.eventsSummaryNotification.length) {
      this.initEvents();
    }

    if (this.companiesSummaryNotification.length) {
      this.initCompanies();
    }
    this.updateListNotification(this.currentPage);
  },

  methods: {
    async paginate(page) {
      await this.updateListNotification(page);
      const url = new URL(window.location.href);
      url.searchParams.set("page", page);
      window.history.replaceState(null, null, url);
    },
    initCategories() {
      if (!this.categories.length) {
        this.categories = [];

        this.categories.push({
          value: "",
          label: this.$t("notification.allNotifications"),
          selected: true
        });
        this.categoriesNotificationEmail.forEach(el => {
          this.categories.push({
            value: el.code,
            label: i18n.locale === "en" ? el.name_eng : el.name_rus,
            selected: false
          });
        });
      }
    },

    initEvents() {
      this.events = [];

      this.events.push({
        value: "",
        label: this.$t("notification.allNotifications"),
        selected: true
      });

      this.eventsSummaryNotification.forEach(item => {
        this.events.push({
          value: item.id,
          label: this.locale === "en" ? item.name_eng : item.name_rus,
          selected: false
        });
      });
    },

    initCompanies() {
      this.listCompanies = this.companiesSummaryNotification
        .filter(
          item => (i18n.locale === "en" && item.name_eng) || item.name_rus
        )
        .map(item => {
          return {
            id: item.id,
            name: i18n.locale === "en" ? item.name_eng : item.name_rus
          };
        });
    },

    async setReadAllNotification() {
      try {
        this.showLoader = true;
        await this.$store.dispatch("notification/readAll", this.apiParams);
      } finally {
        this.showLoader = false;
      }
    },

    async updateListNotification(page) {
      try {
        this.showLoader = true;
        if (page) this.currentPage = +page;

        await this.$store.dispatch(
          "notification/getFullListNotification",
          this.apiParams
        );
      } finally {
        this.showLoader = false;
      }
    },

    changeSearchCompanies(data) {
      this.selectedCompany = null;
      this.getListCompaniesSearch(data);
    },

    getListCompaniesSearch(data) {
      this.isShowSearchList = true;
      if (this.listCompanies && this.listCompanies.length) {
        this.listSearchCompany = data
          ? this.listCompanies.filter(el =>
              el?.name?.toLowerCase().includes(data.toLowerCase())
            )
          : this.listCompanies.filter(() => true);
      }
    },

    selectCompany(data) {
      this.hideSearchList();
      this.searchText = data.name;

      const _this = this;
      setTimeout(function() {
        _this.selectedCompany = data;
      }, 100);
    },
    hideSearchList() {
      this.isShowSearchList = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.notifications {
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  &__header {
    width: 100%;
    box-sizing: border-box;
    padding: 38px 0 48px 0;

    @include respond-down(md) {
      width: calc(100% - 32px);
      padding: 24px 0;
    }

    &-title {
      @include h1();

      @include respond-down(md) {
        font-size: 20px;
      }
    }
  }

  &__content {
    overflow-x: hidden;
    flex-grow: 1;
    background: #f9f9f9;
    padding: 40px 0 56px 0;
    display: flex;
    flex-direction: column;

    @include respond-down(md) {
      padding: 22px 0 32px 0;
    }
    & .container {
      width: 95%;
    }
  }

  &__btn-show-more {
    margin-top: 26px;
    margin-bottom: 30px;
    text-align: center;

    &-link {
      font-weight: 500;
      font-size: 14px;
      line-height: 21px;
      color: #009ee3;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

.settings-notifications {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-content: flex-start;
  align-items: center;
  margin-bottom: 45px;
  padding-right: 63px;

  @include respond-down(lg) {
    margin-left: -8px;
    margin-right: -8px;
    padding-right: 0;
  }
  @include respond-down(sm) {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 30px;
  }
  &__filter {
    max-width: 302px;
    flex: 1;
    padding-right: 20px;

    @include respond-down(lg) {
      flex: none;
      max-width: none;
      width: calc(50% - 16px);
      margin-bottom: 20px;
      padding-right: 8px;
      padding-left: 8px;
    }
    @include respond-down(sm) {
      width: 100%;
      margin-bottom: 15px;
      padding: 0;
    }
    &-title {
      margin-bottom: 8px;
      font-size: 12px;
      line-height: 16px;
      color: #6d7179;
    }
  }
  &__btn-wrap {
    display: inline-block;
    margin-top: 24px;

    @include respond-down(lg) {
      display: block;
      width: 100%;
      margin-top: 0;
      padding-left: 8px;
    }
    @include respond-down(sm) {
      margin-top: 5px;
      padding-left: 0;
    }
  }
  &__btn-select-all {
    display: inline-flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #8c9199;
    cursor: pointer;
    user-select: none;

    .svg-icon {
      margin-right: 8px;
    }
  }
}
</style>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.settings-notifications {
  &__filter {
    &-field:first-child {
      z-index: 15;
    }
    .dropdown__header {
      width: 100%;
      margin: 0;
    }
  }
}

.search-notifications {
  display: block;
  width: 100%;
  margin-bottom: 30px;

  @include respond-down(lg) {
    margin-bottom: 22px;
  }
  @include respond-down(md) {
    margin-bottom: 15px;
  }
  .search-notifications__search {
    max-width: 100%;
    width: 622px;

    @include respond-down(lg) {
      width: 100%;
    }
  }
  .search-field__input {
    background: transparent;

    &::placeholder {
      color: #6d7179;
    }
  }
  .search-field__icon-find,
  .search-field__icon-delete {
    color: #6d7179;
  }
}
</style>
